/**
 * What is true about a position *before* anyone moves.
 *
 * Review answers "what did that move cost?" once the game is over. This answers
 * the question a player actually has while it is still their turn: what is on
 * the board right now, and what will this move let them do to me?
 *
 * Everything here is proven by replaying the position through the interpreter,
 * never estimated. The engine's score can rank a move but cannot explain one,
 * and a coach that guesses is worse than a coach that is quiet — a learner has
 * no way to tell an invented reason from a real one, so one wrong explanation
 * poisons every correct one that follows.
 *
 * Cost is bounded and small: at most `cols` moves tried, each followed by at
 * most `cols` replies, so a standard board is under fifty `applyMove` calls.
 * That is nothing beside the search, which is why this can run on every turn
 * without a budget.
 *
 * Pure: no clock, no randomness, no I/O.
 */
import { applyMove, boardSize, legalMoves } from '../spec/interpreter.ts';
import type { SeatOf, State } from '../spec/interpreter.ts';
import type { RulesSpec } from '../spec/schema.ts';
import { winningMovesFor } from './reasons.ts';

/**
 * A move that loses on the spot, and the reply that punishes it.
 *
 * The reply is carried because "column 3 loses" is a fact a learner has to take
 * on trust, while "column 3 loses to column 5" is one they can play out.
 */
export interface Trap {
  readonly param: number;
  /** The opponent replies that win. Never empty. */
  readonly refutedBy: readonly number[];
}

/** How far into the game the position is, by how full the board is. */
export type Phase = 'opening' | 'middlegame' | 'endgame';

export interface PositionCoaching {
  /** The seat these facts are written for: the one about to move. */
  readonly seat: number;
  /** Moves that complete a line right now. Playing any of them ends the game. */
  readonly winsNow: readonly number[];
  /**
   * Squares where the opponent completes a line if they are left alone.
   *
   * More than one means the game is already lost with correct play — you cannot
   * block two squares with one disc — which is worth saying plainly rather than
   * letting a learner discover it by losing.
   */
  readonly mustBlock: readonly number[];
  /** Legal moves that hand the opponent an immediate win. */
  readonly traps: readonly Trap[];
  /**
   * Moves that leave two winning squares at once.
   *
   * The winning idea in this game, and the one beginners meet last: only one
   * can be answered, so the second wins.
   */
  readonly forks: readonly number[];
  /** Legal moves that are not traps. Empty means every move loses. */
  readonly safe: readonly number[];
  readonly phase: Phase;
  /** Every legal move, for callers that need the denominator. */
  readonly legal: readonly number[];
}

/** Board fullness, as a fraction, deciding which advice is worth giving. */
function fullness<S extends RulesSpec>(rules: S, state: State<S>): number {
  const size = boardSize(rules);
  if (size === 0) return 1;
  let filled = 0;
  for (let index = 0; index < size; index += 1) {
    if (state.board[index] !== rules.cells.empty) filled += 1;
  }
  return filled / size;
}

/**
 * Phase by how full the board is, not by move number.
 *
 * Derived rather than hard-coded so the boundaries mean the same thing on a
 * board of any size. The opening ends at roughly a fifth full, which on a
 * standard 7x6 board is the eight or so discs after which both players have a
 * real position and principles stop being the most useful thing to say.
 */
export function phaseOf<S extends RulesSpec>(rules: S, state: State<S>): Phase {
  const filled = fullness(rules, state);
  if (filled < 0.2) return 'opening';
  if (filled < 0.65) return 'middlegame';
  return 'endgame';
}

/**
 * Read the position from the point of view of the seat to move.
 *
 * Returns empty facts for a finished game rather than throwing: the caller is
 * usually a render, and a coach panel that crashes the board it sits beside is
 * a worse outcome than one that has nothing to say.
 */
export function readPosition<S extends RulesSpec>(
  rules: S,
  state: State<S>,
): PositionCoaching {
  const seat = state.toMove;
  const empty: PositionCoaching = {
    seat,
    winsNow: [],
    mustBlock: [],
    traps: [],
    forks: [],
    safe: [],
    phase: phaseOf(rules, state),
    legal: [],
  };
  if (state.status.kind !== 'in_play') return empty;

  const opponent = otherSeat(rules, seat);
  const legal = legalMoves(rules, state).map((move) => move.param);

  const winsNow = winningMovesFor(rules, state, seat);
  const mustBlock = winningMovesFor(rules, state, opponent);

  const traps: Trap[] = [];
  const forks: number[] = [];
  const safe: number[] = [];

  for (const param of legal) {
    const applied = applyMove(rules, state, seat, { param });
    if (!applied.ok) continue;

    // A move that ends the game is neither a trap nor a fork: there is no reply.
    if (applied.state.status.kind !== 'in_play') {
      safe.push(param);
      continue;
    }

    const replies = winningMovesFor(rules, applied.state, opponent);
    if (replies.length > 0) {
      traps.push({ param, refutedBy: replies });
      continue;
    }

    safe.push(param);

    // Two winning squares of our own, with the opponent holding one disc: they
    // block one, we play the other.
    if (winningMovesFor(rules, applied.state, seat).length >= 2) forks.push(param);
  }

  return {
    seat,
    winsNow,
    mustBlock,
    traps,
    forks,
    safe,
    phase: phaseOf(rules, state),
    legal,
  };
}

/** The seat that is not `seat`. Coaching is written for two-player games. */
function otherSeat<S extends RulesSpec>(rules: S, seat: number): SeatOf<S> {
  const other = rules.seats.find((candidate) => candidate !== seat);
  return (other ?? seat) as SeatOf<S>;
}

/**
 * The one thing most worth saying about this position, or nothing.
 *
 * Ordered by what actually decides games, which is not the same as what is most
 * interesting: a win on the board outranks a threat against you, and both
 * outrank any amount of positional advice. Only one is returned because a coach
 * that lists four things during your turn is a coach you stop reading.
 */
export type Urgency =
  /** You have a line to complete. */
  | { readonly kind: 'win_available'; readonly params: readonly number[] }
  /** They complete a line next move unless this square is taken. */
  | { readonly kind: 'must_block'; readonly param: number }
  /** Two of their squares are live; one disc cannot cover both. */
  | { readonly kind: 'lost_to_double'; readonly params: readonly number[] }
  /** A move here leaves two winning squares. */
  | { readonly kind: 'fork_available'; readonly params: readonly number[] }
  /** Every legal move hands the game over. */
  | { readonly kind: 'all_moves_lose' }
  /** Some moves lose on the spot; the rest are playable. */
  | { readonly kind: 'avoid_traps'; readonly traps: readonly Trap[] };

export function urgencyOf(coaching: PositionCoaching): Urgency | null {
  if (coaching.legal.length === 0) return null;

  // Winning ends the argument. Nothing else on the board matters.
  if (coaching.winsNow.length > 0) {
    return { kind: 'win_available', params: coaching.winsNow };
  }

  if (coaching.mustBlock.length > 1) {
    return { kind: 'lost_to_double', params: coaching.mustBlock };
  }
  const block = coaching.mustBlock[0];
  if (block !== undefined) return { kind: 'must_block', param: block };

  if (coaching.safe.length === 0) return { kind: 'all_moves_lose' };

  if (coaching.forks.length > 0) {
    return { kind: 'fork_available', params: coaching.forks };
  }

  if (coaching.traps.length > 0) return { kind: 'avoid_traps', traps: coaching.traps };

  return null;
}
